import { useColorContext } from "@ihs-widget/stores/ColorContext";
import { useTheme } from "@ihs-widget/stores/ThemeContext";
import { T } from "@ihs-widget/utils/enums";
import { useVersion } from "@ihs-widget/utils/hooks/useVersion";
import React, { useMemo } from 'react';

interface ScoreColumnProps {
    name?: string;
    status: string;
    games: number;
    color: string;
    team: 'home' | 'center' | 'away';
}

interface TextLabelProps {
    text?: string;
    additionalClass?: string;
}

interface ScoreBoxProps {
    games: number;
    color: string;
    theme: string;
    team: 'home' | 'center' | 'away';
}

const TextLabel = ({ text, additionalClass }: TextLabelProps) => {
    const baseClass = `text-[12px] leading-[16px] text-base120 dark:text-monoY font-robotoRegular font-normal
        text-left max-w-full
        whitespace-nowrap text-ellipsis overflow-x-hidden overflow-y-visible
        [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]`;

    const fullClass = `${baseClass} ${additionalClass || ''}`;

    return <div className={fullClass}>{text}</div>;
};

const ScoreBox = React.memo(({ games, color, theme, team }: ScoreBoxProps) => {
    const { colors } = useColorContext();
    const { isV1, isV2 } = useVersion();

    const gamesTextColor = useMemo(() => {
        if (isV1) {
        switch (color) {
            case 'success110':
                return theme === T.DARK ? 'monoX' : 'monoY';
            case 'attention110':
                return theme === T.DARK ? 'monoX' : 'monoY';
            default:
                    return theme === T.DARK ? 'monoX' : 'monoX';
            }
        } else if (isV2) {
            if (team === 'home') {
                return colors?.textAdjustedToBGHome;
            } else if (team === 'away') {
                return colors?.textAdjustedToBGAway;
            } else if (team === 'center') {
                return '#000000';
            } else {
                return '#000000';
            }
        }
    }, [color, theme, isV1, isV2]);

    const bgColorClass = useMemo(() => {


        if (isV1) {
            if (theme === T.DARK) {
                switch (color) {
                    case 'success090': return 'bg-success090';
                    case 'base090': return 'bg-base090';
                    case 'interactive090': return 'bg-interactive090';
                    default: return 'bg-transparent';
                }
            } else {
                switch (color) {
                    case 'success110': return 'bg-success110';
                    case 'base090': return 'bg-base090';
                    case 'attention110': return 'bg-attention110';
                    default: return 'bg-transparent';
                }
            }
        } else if (isV2) {
            if (team === 'home') {
                return colors?.homeColor;
            } else if (team === 'away') {
                return colors?.awayColor;
            } else if (team === 'center') {
                return '#CCCCCC';
            } else {
                return 'transparent';
            }
        }

    }, [color, theme]);

    return (
        <div className={`text-[12px] leading-[16px] font-robotoRegular font-normal
                        w-[32px] h-[24px] flex justify-center items-center ${bgColorClass}
                        rounded-[6px] mb-[4px] text-${gamesTextColor}`}
                        style={{backgroundColor: bgColorClass, color: gamesTextColor, border: isV2 ? `0.6px solid ${colors?.borderColor}` : 'none'}}>
            {games}
        </div>
    );
});

const ScoreColumn = ({ name, status, games, color, team }: ScoreColumnProps) => {
    const { theme } = useTheme();

    return (
        <div className='min-w-[75px] max-w-[75px] 
                        sm351:min-w-[95.67px] sm351:max-w-[95.67px] 
                        h-[64px] flex flex-col justify-start items-center'>
            <TextLabel text={name} additionalClass="mb-[4px] h-[16px]" />
            <ScoreBox games={games} color={color} theme={theme} team={team} />
            <TextLabel text={status} />
        </div>
    )
}

export default ScoreColumn;